import React from 'react';
import DataTable from 'react-data-table-component';
import Spinner from './Spinner';
import styled from 'styled-components';

const ModernSubTable = styled(DataTable)`
    .rdt_Table {
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #e2e8f0;
    }

    .rdt_TableRow {
        border-bottom: 1px solid #f1f5f9;
        transition: background-color 0.15s ease;
    }

    .rdt_TableRow:hover {
        background-color: #f8fafc;
    }

    .rdt_TableHeadRow {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-bottom: 1px solid #e2e8f0;
        position: relative;
        font-weight: 600;
        color: #374151;
    }

    .rdt_TableHeadRow::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
        opacity: 0.8;
    }

    .rdt_TableHeader {
        font-size: 0.75rem;
        font-weight: 600;
        color: #374151;
        padding: 12px 8px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .rdt_TableCell {
        padding: 10px 8px;
        font-size: 0.875rem;
        color: #1f2937;
    }

    .rdt_Pagination {
        border-top: 1px solid #f1f5f9;
        background: #fafbfc;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
    }
`;

const SubTableV2 = ({ columns, data, loading, alertMessage, clearAlert, title, children, infoMessage }) => {
  // Memoize columns setup
  const columnsSetup = React.useMemo(() => columns, [columns]);

  return (
    <div className="mb-6">
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      )}
      {alertMessage && (
        <div className={`alert p-4 rounded-lg mb-4 ${
          alertMessage.startsWith('Successfully')
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        } flex justify-between items-center shadow-sm`}>
          {alertMessage}
          <button onClick={clearAlert} className="text-lg font-bold hover:opacity-70">×</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {children && (
              <div className="flex items-center gap-2">
                {children}
              </div>
            )}
          </div>
        </div>

        {/* Info Message */}
        {infoMessage && (
          <div className="bg-blue-50 border-b border-blue-200 text-blue-700 p-3" role="alert">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">{infoMessage}</span>
            </div>
          </div>
        )}

        {/* Table Content */}
        <div className="overflow-hidden">
          <ModernSubTable
            columns={columnsSetup}
            data={data}
            pagination
            highlightOnHover
            responsive
            noHeader
            customStyles={{
              table: {
                style: {
                  backgroundColor: 'white',
                  margin: 0,
                },
              },
              headCells: {
                style: {
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#374151',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                },
              },
              cells: {
                style: {
                  fontSize: '0.875rem',
                  color: '#1f2937',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                },
              },
              pagination: {
                style: {
                  borderTop: '1px solid #f1f5f9',
                  backgroundColor: '#fafbfc',
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px',
                },
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SubTableV2;
